<script setup lang="ts">
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import AddGreigForm from '@/components/features/batches/forms/AddGreigForm.vue'
import { BatchState, type MergeUsage } from '@/types/batch-types'
import { useBatchForm } from '@/composables/useBatchForm'
import { addBatch } from '@/api/batches'
import { onMounted, ref } from 'vue'

const { values, handleSubmit, showToast, formType, loading, errors , setFieldValue} = useBatchForm('add')

const router = useRouter()
const route = useRoute()
const batchState = route.query.state ?? (BatchState.GREIG as string)

const defaultCurrency = ref(values.costPerKgCurrency);

const submit =handleSubmit( async () => {
  if(!values.batchNo || values.batchNo.trim() === '') {
    showToast('error', 'Validation Error', 'Batch number is required.')
    return
  }

  if(!values.knittingQuality?.unfinishedReference) {
    showToast('error', 'Validation Error', 'Please select a quality inorder to select a merge.')
    return
  }

  if(!values.selectedMerges || values.selectedMerges.length === 0) {
    showToast('error', 'Validation Error', 'Please select at least one merge.')
    return
  }

  const hasEmptyRollWeights = values.rolls?.rolls.some((roll: { weight: number }) => !roll.weight || roll.weight <= 0)
  if(hasEmptyRollWeights) {
    showToast('error', 'Validation Error', 'Please enter weights for all rolls.')
    return
  }

  if(!values.dateKnitted) {
    showToast('error', 'Validation Error', 'Please select a date knitted.')
    return
  }

  if(!values.machineNumber) {
    showToast('error', 'Validation Error', 'Machine number is required.')
    return
  }

  const hasInvalidYarnValues = values.mergeUsage.some((merge: MergeUsage) => {
    return (merge.kiloUsed !== null && merge.kiloUsed < 0) ||
           (merge.kiloWasted !== null && merge.kiloWasted < 0)
  })
  if(hasInvalidYarnValues) {
    showToast('error', 'Validation Error', 'Kilo of yarn used and wasted cannot be less than zero.')
    return
  }

  loading.value = true
  const formValues = {
    batchNumber: values.batchNo,
    knittingSupplierId: values.knittingSupplier.id,
    knittingQualityId: values.knittingQuality.id,
    dateKnitted: values.dateKnitted.toISOString(),
    weight: values.rolls.rolls.reduce(
      (acc: number, roll: { weight: number }) => acc + roll.weight,
      0
    ),
    bay: values.bay,
    machineNumber: values.machineNumber,
    purchaseOrderReference: values.purchaseOrderReference,
    notes: values.notes,
    createdAt: new Date().toISOString(),
    rolls: values.rolls.rolls.map((roll: { rollNumber: any; lengthValue: any; weight: any }) => ({
      rollNumber: roll.rollNumber,
      lengthValue: roll.lengthValue || 0,
      weight: roll.weight,
      dateKnitted: new Date().toISOString(),
      rollState: 0,
      bay: values.bay,
      lastEditedBy: '',
      isActive: true
    })),
    batchMerges: values.mergeUsage.map((merge: MergeUsage) => ({
      mergeId: merge.mergeId,
      kiloOfYarnUsed: merge.kiloUsed,
      kiloOfYarnWasted: merge.kiloWasted
    }))
  }

  try {
    const response = await addBatch(formValues)
    loading.value = false
    showToast('success', 'Batch Added!', 'You have successfully added a new batch.')
    const newBatchId = response.id
    await router.push(`/batches/${newBatchId}`)
  } catch (error: any) {
    loading.value = false

    if (error.response && error.response.data && error.response.data.errors) {
      const errors = error.response.data.errors
      const errorMessage = errors
        .map((err: { code: string; description: string }) => err.description)
        .join('\n')
      showToast('error', 'Validation Error', errorMessage)
    } else if (error.response) {
      showToast(
        'error',
        'Error',
        `Server Error: ${error.response.status} - ${error.response.data.title || error.message}`
      )
    } else if (error.request) {
      showToast('error', 'Error', 'No response from the server.')
    } else {
      showToast('error', 'Error', `Request setup error: ${error.message}`)
    }
  }
},
(errors: Record<string, string>) => {
  loading.value = false

  const errorObjects = Object.entries(errors.errors).map(([field, message]) => ({
    code: field,
    description: message,
  }))

  if(errorObjects.length > 0) {
    const firstError = errorObjects[0]
    const errorMessage = ` ${firstError.description}`
    showToast('error', 'Validation Error', errorMessage)
    return
  }
}
)

onMounted(() => {
  defaultCurrency.value = 0
  setFieldValue('costPerKgCurrency', defaultCurrency.value)
})
</script>

<template>
  <div class="p-10 bg-white rounded-xl">
    <div class="flex justify-between items-center mb-10">
      <RouterLink to="/batches" class="flex items-center gap-x-2 group hover:text-primary-default">
        <Icon
          icon="material-symbols:arrow-back-rounded"
          class="text-3xl group-hover:translate-x-2 transition-transform"
        />
        <span>Back</span>
      </RouterLink>

      <h1 class="font-semibold text-2xl">Adding New Batch</h1>

      <Button label="Save Batch" @click="submit">
        <template #icon>
          <Icon icon="material-symbols:save" class="text-xl ml-3 order-1" />
        </template>
      </Button>
    </div>

    <AddGreigForm
      v-if="batchState === BatchState.GREIG"
      :type="formType"
      :values="values"
      :errors="errors"
      @update:values="(newValues) => Object.assign(values, newValues)"
    />
  </div>
</template>

<style scoped></style>
